import React from 'react'
import map from 'lodash/map'
import type { Task } from '@ztechapps/common.contract'
import { TaskStatus } from '@ztechapps/common.contract'
import { AvatarGroup, HorizontalStack, Icon, resolveStatusColor, ShowIf, Spinner } from '@ztechapps/webui.util'
import { LocalDateTime } from '@ztechapps/webui.date'
import { useFormatWorkdayTime } from '@ztechapps/common.hook'
import styles from './TaskBoard.module.scss'

type TaskBoardCardProps = {
  task: Task
  isTransitioning?: boolean
  onDoubleClick?: (taskId: string, event: React.MouseEvent) => void
}

const MAX_AVATARS = 3

const statusColorMap = Object.values(TaskStatus).reduce(
  (acc, s) => {
    acc[s.value] = resolveStatusColor(s)?.fill
    return acc
  },
  {} as Record<string, string>
)

const buildMemberAvatars = (members: Task['members']) => {
  return map(members, (m) => {
    let picture = m?.profile?.picture ?? ''
    if (picture?.includes('/dft-customer.jpg')) {
      picture = ''
    }
    return { name: m?.profile?.name, src: picture }
  })
}

export const TaskBoardCard = ({ task, isTransitioning, onDoubleClick }: TaskBoardCardProps) => {
  const { formatTime } = useFormatWorkdayTime()
  const borderColor = statusColorMap[task.status] ?? 'var(--bs-border-color)'
  const hasProject = !!task.project?.code
  const hasWorkOrder = !!task.workOrder?.code
  const hasMeta = hasProject || hasWorkOrder
  const hasMembers = (task.members?.length ?? 0) > 0
  const hasTimeSpent = (task.timeSpent ?? 0) > 0
  const hasEndDate = !!task.endDate
  const subtaskCount = task.subTasks?.length ?? 0
  const hasSubtasks = subtaskCount > 0
  const memberAvatars = hasMembers ? buildMemberAvatars(task.members) : []
  const overflowCount = memberAvatars.length - MAX_AVATARS

  return (
    <div
      className={`${styles.card} ${isTransitioning ? styles.cardTransitioning : ''} bg-body border-top border-end border-bottom rounded-2 position-relative`}
      style={{ borderLeftColor: borderColor }}
      onDoubleClick={(e) => onDoubleClick?.(task._id, e)}
    >
      <ShowIf condition={!!isTransitioning}>
        <div
          className={`${styles.cardTransitionOverlay} position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center rounded-2`}
        >
          <Spinner size="sm" />
        </div>
      </ShowIf>

      <HorizontalStack gap={2} className="justify-content-between">
        <div className="fw-medium text-truncate text-body">{task.name}</div>
        <ShowIf condition={hasEndDate}>
          <small className="text-secondary text-nowrap">
            <Icon name="calendar" className="me-1" />
            <LocalDateTime value={task.endDate} />
          </small>
        </ShowIf>
      </HorizontalStack>

      <ShowIf condition={hasMeta}>
        <HorizontalStack gap={1} className={`${styles.cardMeta} text-truncate text-secondary small`}>
          <ShowIf condition={hasProject}>
            <span>{task.project?.code}</span>
          </ShowIf>
          <ShowIf condition={hasProject && hasWorkOrder}>
            <span className="fw-bold">&middot;</span>
          </ShowIf>
          <ShowIf condition={hasWorkOrder}>
            <span>{task.workOrder?.code}</span>
          </ShowIf>
        </HorizontalStack>
      </ShowIf>

      <ShowIf condition={hasMembers || hasTimeSpent || hasSubtasks}>
        <HorizontalStack gap={2} className={`${styles.cardFooter} justify-content-between`}>
          <ShowIf condition={hasMembers}>
            <HorizontalStack className="flex-shrink-0">
              <AvatarGroup dataArray={memberAvatars} size={24} max={MAX_AVATARS} />
              <ShowIf condition={overflowCount > 0}>
                <small className="fw-semibold text-secondary ms-1">+{overflowCount}</small>
              </ShowIf>
            </HorizontalStack>
          </ShowIf>

          <HorizontalStack gap={2} className="ms-auto">
            <ShowIf condition={hasTimeSpent}>
              <small className="text-secondary text-nowrap">
                <Icon name="clock" className="me-1" />
                {formatTime(task.timeSpent)}
              </small>
            </ShowIf>
            <ShowIf condition={hasSubtasks}>
              <small className="text-secondary text-nowrap">
                <Icon name="list-check" className="me-1" />
                {subtaskCount} {subtaskCount === 1 ? 'subtask' : 'subtasks'}
              </small>
            </ShowIf>
          </HorizontalStack>
        </HorizontalStack>
      </ShowIf>
    </div>
  )
}
